import { useState } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { CHECKOUT } from "@/constants/testIds";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const IN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim",
  "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal",
  // Union Territories
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, loading } = useAuth();
  const { state } = useLocation();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "",
    line1: "", city: "", state: "", pincode: "",
  });

  // Hard guard: render nothing while auth resolves, redirect during render if logged out.
  // <Navigate> instead of useEffect prevents any form flash before redirect.
  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/checkout" replace />;

  // GST 18% rounded to nearest rupee (half-up). Total is computed from
  // the SAME rounded tax so display math always adds up — previously
  // tax and total were rounded independently which could disagree by ₹1.
  const tax = Math.round(subtotal * 0.18);
  const grandTotal = subtotal + tax;

  const placeOrder = async () => {
    if (!user) { nav("/login?next=/checkout", { replace: true }); return; }
    if (items.length === 0) { toast.error("Cart is empty"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        affiliate_code: state?.affiliate || undefined,
        shipping_address: form,
      });
      // Razorpay stub — confirm immediately
      const { data: confirmed } = await api.post(`/orders/${data.order.id}/confirm`);
      toast.success("Order placed!");
      clear();
      // Send the confirmed order in nav state so the confirmation page
      // renders instantly with no fetch. It falls back to GET /orders/:id
      // if state is missing (e.g. hard refresh on the confirmation URL).
      nav(`/orders/${data.order.id}`, { state: { order: confirmed || data.order }, replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  if (items.length === 0) {
    return <div className="py-32 text-center text-white/60">Your cart is empty. <Link to="/marketplace" className="text-accent hover:text-white">Browse the marketplace</Link></div>;
  }

  return (
    <div className="py-20">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter">Checkout</h1>
        <div className="mt-10 grid lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="font-display text-lg">Shipping</h3>
            <input data-testid={CHECKOUT.shippingName} placeholder="Full name" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            <input data-testid={CHECKOUT.shippingPhone} placeholder="Phone" value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            <input data-testid={CHECKOUT.shippingLine1} placeholder="Address" value={form.line1}
                   onChange={(e) => setForm({ ...form, line1: e.target.value })}
                   className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid={CHECKOUT.shippingCity} placeholder="City" value={form.city}
                     onChange={(e) => setForm({ ...form, city: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
              <input data-testid={CHECKOUT.shippingPincode} placeholder="PIN" value={form.pincode}
                     onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                     className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD]" />
            </div>
            <select data-testid={CHECKOUT.shippingState}
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className={`w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#9D4CDD] ${form.state ? "text-white" : "text-white/40"}`}>
              <option value="" className="bg-[#11131A]">State…</option>
              {IN_STATES.map((s) => (
                <option key={s} value={s} className="bg-[#11131A] text-white">{s}</option>
              ))}
            </select>
          </div>

          <div className="glass rounded-2xl p-6">
            <h3 className="font-display text-lg">Review</h3>
            <div className="mt-4 space-y-2 max-h-72 overflow-y-auto scrollbar-hide">
              {items.map((i) => (
                <div key={i.product_id} className="flex justify-between text-sm py-2 border-b border-white/5">
                  <span className="text-white/70 truncate pr-2">{i.name} × {i.qty}</span>
                  <span className="text-accent">₹{(i.price * i.qty).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-white/60">GST 18%</span><span>₹{tax.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between font-display text-lg mt-3"><span>Total</span><span className="text-accent">₹{grandTotal.toLocaleString("en-IN")}</span></div>
            </div>
            <button data-testid={CHECKOUT.placeOrder} onClick={placeOrder} disabled={busy}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full gradient-bg font-medium disabled:opacity-50">
              {busy ? "Placing…" : "Place order"}
            </button>
            <div className="mt-4 flex items-center gap-2 text-xs text-white/40">
              <ShieldCheck className="w-3.5 h-3.5" />
              Payments handled securely by Razorpay.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
